<template>
    <div class="mt-4">
        <!-- UPDATE PAYMENT MODAL -->
        <b-modal id="updatePaymentModal"
                 title="Wijzig betaling"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="updatePayment"
        >
            <b-row class="mb-2">
                <b-col cols="4">Nieuwe methode</b-col>
                <b-col cols="5" v-if="editPayment">
                    <span v-if="allowedPaymentMethods.length > 1">
                        <div v-for="paymentMethod in this.allowedPaymentMethods" :key="paymentMethod.identifier">
                            <input name="paymentMethod" type="radio" :id="paymentMethod.identifier" :value="paymentMethod.identifier" v-model.trim="selectedPaymentMethodIdentifier" class="mr-2">
                            <label :for="paymentMethod.identifier">{{ paymentMethod.display_name }}</label>
                        </div>
                    </span>
                    <span v-else>{{ selectedPaymentMethodName }}</span>
                </b-col>
            </b-row>
            <template #modal-footer="{ cancel, ok }">
                <b-btn variant="secondary" @click="cancel">Annuleren</b-btn>
                <b-btn variant="primary" :disabled="currentPaymentMethodIdentifier === selectedPaymentMethodIdentifier" @click="ok">OK</b-btn>
            </template>
        </b-modal>

        <BRow class="mb-4">
            <BCol cols="12" md="6" class="mb-4">
                <OmniCard>
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center">
                            <BCardTitle class="text-uppercase">
                                Informatie
                            </BCardTitle>

                            <BModal
                                id="inspect-sales-order-metadata-modal"
                                title="Extra order data"
                                size="lg"
                                centered
                                dialog-class="shadow-none"
                                ok-only
                            >
                                <ShowSalesOrderMetadata
                                    :salesOrder="salesOrder"
                                />
                            </BModal>
                        </div>
                    </template>

                    <template #default>
                        <OmniTableSmall :fields="informationFields" :items="informationFieldItems">
                            <template v-slot:cell(salesOrder_identifier)="data">
                                <FontAwesomeIcon v-if="useIconForCancelledOrder && isOrderCancelled({ salesOrder })" icon="ban" class="text-danger mr-1" title="Order volledig geannuleerd" />
                                <span :class="{ 'text-decoration-line-through': isOrderCancelled({ salesOrder }) && strikeThroughCancelledOrder }">{{ data.item['salesOrder_identifier'] }}</span>
                            </template>
                        </OmniTableSmall>
                    </template>

                    <template #footer>
                        <OmniButtonCollection direction="horizontal">
                                <OmniButton v-if="canInspectMetadata"
                                            @click="$bvModal.show('inspect-sales-order-metadata-modal')"
                                            icon="glasses"
                                >
                                    Bekijk meer info
                                </OmniButton>

                                <OmniButton v-if="canInspectMetadata"
                                            @click="openLink(`/api/rest/sales-orders/${salesOrder.identifier}/order-create-payload`)"
                                            icon="far fa-file-alt"
                                >
                                    Bekijk ruwe data
                                </OmniButton>
                            </OmniButtonCollection>
                    </template>
                </OmniCard>
            </BCol>

            <BCol cols="12" md="6" class="mb-4">
                <OmniCard>
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center">
                            <BCardTitle class="text-uppercase">
                                Notities
                            </BCardTitle>
                        </div>
                    </template>

                    <template #default>
                        <OrderCommentsHistory
                            :salesOrderIdentifier="salesOrder.identifier"
                            class="limited-height"
                            ref="orderCommentsHistory"
                            @edit="comment => openEditCommentModal(comment)"
                        />

                        <BModal
                            id="add-comment-form"
                            :title="editComment ? `Notitie wijzigen` : `Nieuwe notitie plaatsen bij order`"
                            size="xl"
                            dialog-class="shadow-none"
                            footer-class="d-none"
                            centered
                            hide-header-close
                        >
                            <OrderCommentsForm
                                :salesOrderIdentifier="salesOrder.identifier"
                                :edit-comment="editComment"
                                @close="editComment = null; $bvModal.hide('add-comment-form')"
                                @saved="handleSalesOrderCommentSaved"
                            />
                        </BModal>
                    </template>

                    <template #footer>
                        <OmniButtonCollection direction="horizontal" v-if="hasPermission('orders.addcomment')">
                            <OmniButton
                                @click="$bvModal.show('add-comment-form')"
                                size="sm"
                                class="mr-1 pt-1"
                                icon="circle-plus"
                                >
                                    Notitie toevoegen
                            </OmniButton>
                        </OmniButtonCollection>
                    </template>
                </OmniCard>
            </BCol>
        </BRow>

      <BRow class="mb-2">
            <BCol cols="12" md="6" class="mb-4">
                <OmniCard>
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center">
                            <BCardTitle class="text-uppercase">
                                <template v-if="salesOrder.fulfilment_method === 'pickup'">
                                    {{ $t('omt.gui.orders.label.address-details.order-pickup') }}
                                </template>
                                <template v-else>
                                    {{ $t('omt.gui.orders.label.address-details.order-shipping') }}
                                </template>
                            </BCardTitle>

                            <OmniButton v-if="hasAllPermissions('orders.address.edit', 'orders.edit')"
                                    v-show="!editingAddressShipping"
                                    @click="editAddressInformation('shipping')"
                                    icon="pencil"
                                    variant="info"
                                >
                                    Bewerken
                            </OmniButton>
                        </div>
                    </template>

                    <template #default>
                        <AddressTable :address="addressShipping"
                            :editable="hasAllPermissions('orders.address.edit', 'orders.edit')"
                            :editing="editingAddressShipping"
                            :saving="savingAddressShipping"
                            :orderNo="salesOrder.identifier"
                            @edit="editAddressInformation('shipping')"
                            @save="saveAddressInformation('shipping', $event)"
                            @cancel="cancelEditAddressInformation('shipping')"
                        />
                    </template>
                </OmniCard>
          </BCol>

          <BCol cols="12" md="6" class="mb-4">
            <OmniCard>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center">
                        <BCardTitle class="text-uppercase">
                            {{ $t('omt.gui.orders.label.address-details.order-billing') }}
                        </BCardTitle>

                        <OmniButton v-if="hasAllPermissions('orders.address.edit', 'orders.edit')"
                            v-show="!editingAddressBilling"
                            @click="editAddressInformation('billing')"
                            icon="pencil"
                            variant="info"
                        >
                            Bewerken
                        </OmniButton>
                    </div>
                </template>

                <template #default>
                    <AddressTable :address="addressBilling"
                            :editable="hasAllPermissions('orders.address.edit', 'orders.edit')"
                            :editing="editingAddressBilling"
                            :saving="savingAddressBilling"
                            :orderNo="salesOrder.identifier"
                            @save="saveAddressInformation('billing', $event)"
                            @cancel="cancelEditAddressInformation('billing')"
                    />
                </template>
            </OmniCard>
          </BCol>
        </BRow>

        <BRow class="mb-4">
            <BCol cols="12">
                <OmniCard>
                    <template #header>
                        <BCardTitle class="text-uppercase">
                            Leveringen en producten
                        </BCardTitle>

                        <DrqButtons
                            v-if="showDrqButtonsOnTop"
                            :salesOrder="salesOrder"
                            :alignment="drqButtonsAlignment"
                            :isDispatchRequestsLoading="isDispatchRequestsLoading"
                            :dispatchRequestRerouteMode="dispatchRequestRerouteMode"
                            :hasReroutableDispatchRequests="hasReroutableDispatchRequests"
                            @reloadDispatchRequests="reloadDispatchRequests"
                            @toggleRerouteMode="toggleRerouteMode"
                            @openFinalDispatchRequestActionModal="openFinalDispatchRequestActionModal"
                            @showModal="(modal) => $bvModal.show(modal)"
                            class="mt-4"
                        />
                    </template>

                    <template #default>
                        <BRow>
                            <BCol cols="12">
                                <DispatchRequests v-if="dispatchRequests" :dispatch-requests="dispatchRequests"
                                       :sales-order="salesOrder"
                                       @loaded="(...args) => { hasReroutableDispatchRequests = args[0] }"
                                       @dataChanged="reloadDispatchRequests"
                                       @reroutingSuccessful="reloadDispatchRequests"
                                       :class="{'opacity-2': isDispatchRequestsLoading}"
                                       :reroute-mode="false"
                                       ref="dispatchRequestsView"
                                       :key="salesOrder.id"
                                 />

                                <b-alert variant="warning" :show="['on-hold','on-hold-final'].includes(salesOrder.validation_status) && !salesOrder.current_dispatch_requests?.length" class="m-0">
                                    <font-awesome-icon icon="triangle-exclamation" class="text-danger mr-2"/>
                                    Deze order heeft geen leveringen omdat deze is geblokkeerd.
                                    Ga naar het tabblad
                                    <router-link :to="{name: 'OrderDetailHoldReasons', params: {salesOrderIdentifier: salesOrder.identifier}}">blokkeringen</router-link>
                                    om de blokkering te bekijken.
                                </b-alert>
                            </BCol>
                        </BRow>
                    </template>

                    <template #footer>
                       <DrqButtons
                            v-if="showDrqButtonsOnBottom"
                            :salesOrder="salesOrder"
                            :alignment="drqButtonsAlignment"
                            :isDispatchRequestsLoading="isDispatchRequestsLoading"
                            :dispatchRequestRerouteMode="dispatchRequestRerouteMode"
                            :hasReroutableDispatchRequests="hasReroutableDispatchRequests"
                            @reloadDispatchRequests="reloadDispatchRequests"
                            @toggleRerouteMode="toggleRerouteMode"
                            @openFinalDispatchRequestActionModal="openFinalDispatchRequestActionModal"
                            @showModal="(modal) => $bvModal.show(modal)"
                        />

                        <BModal id="add-order-line-modal" title="Orderregel toevoegen"
                                size="xl"
                                centered
                                dialog-class="shadow-none"
                                footer-class="d-none"
                        >
                            <add-sales-order-line :sales-order="salesOrder" @success="salesOrderLineAdded()"/>
                        </BModal>

                        <BModal id="rerouting-modal" title="Rerouting"
                                size="xl"
                                centered
                                dialog-class="shadow-none"
                                footer-class="d-none"
                                v-model="dispatchRequestRerouteMode"
                                @hide="exitRerouteMode"
                        >
                            <DispatchRequests :dispatch-requests="salesOrder.current_dispatch_requests"
                                              :sales-order="salesOrder"
                                              @dataChanged="reloadDispatchRequests"
                                              @cancelRerouteMode="toggleRerouteMode"
                                              @reroutingSuccessful="toggleRerouteMode(false)"
                                              :reroute-mode="true"
                                              :hide-actions="true"
                            />
                        </BModal>

                        <BModal
                            id="inspect-dispatch-request-metadata-modal"
                            :okTitle="$t('omt.gui.general.action.close')"
                            :title="`Ruwe data van order nr. ${salesOrder.identifier}`"
                            size="lg"
                            centered
                            dialog-class="shadow-none"
                            ok-only
                        >
                            <ShowDispatchRequestMetadata
                                :salesOrder="salesOrder"
                            />

                            <template #modal-footer>
                                <OmniButton @click="$bvModal.hide('inspect-dispatch-request-metadata-modal')"
                                            icon="times"
                                >
                                    Sluiten
                                </OmniButton>
                            </template>
                        </BModal>

                        <BModal
                            id="final-dispatch-request-actions-modal"
                            :title="$t(`omt.gui.orders.dispatch-requests.final-action-titles.${finalDispatchRequestAction}`)"
                            size="lg"
                            centered
                            dialog-class="shadow-none"
                            hide-footer
                        >
                            <final-dispatch-request-actions :sales-order="salesOrder" :action="finalDispatchRequestAction"
                                                            @close="$bvModal.hide('final-dispatch-request-actions-modal')"
                                                            @dataChanged="reloadDispatchRequests"
                            />
                        </BModal>
                    </template>
                </OmniCard>
            </BCol>
        </BRow>

        <BRow class="mb-4">
            <BCol cols="12" md="6">
                <OmniCard class="mb-4">
                    <template #header>
                        <BCardTitle>FACTUUR INFORMATIE</BCardTitle>
                    </template>

                    <OmniTableSmall :fields="invoice_fields" :items="invoice_items" />
                </OmniCard>
            </BCol>

            <BCol cols="12" md="6">
                <OmniCard>
                    <template #header>
                        <BCardTitle>BETALINGEN <span class="float-right">Toon historie <input type="checkbox" class="ml-1" v-model="showAllPayments"></span></BCardTitle>
                    </template>

                    <span v-if="isPaymentUpdating">
                        <font-awesome-icon icon="spinner" spin/>
                    </span>
                    <BTable v-else :items="payments" :fields="paymentsFields" hover>
                        <template #cell(datetime)="data">
                            {{ data.value | datetime }}
                        </template>
                        <template #cell(Methode)="data">
                            <span v-text="data.value.payment_service_provider_account_method.display_name"
                                  :title="`${data.value.payment_service_provider_account.payment_service_provider.name} (${data.value.payment_service_provider_account.identifier})`"
                            />

                        </template>
                        <template #cell(Bedrag)="data">
                            <span v-if="data.value">{{ data.value | price }}</span>
                            <i v-else>onbekend</i>
                        </template>
                        <template #cell(actions)="data">
                            <span class="ml-1">
                                <b-btn variant="link" size="sm" @click="openEditPaymentModal(data.item)" class="py-0" v-if="isPaymentEditable(data.item)">wijzigen</b-btn>
                            </span>
                        </template>
                    </BTable>

                </OmniCard>
            </BCol>
        </BRow>
    </div>
</template>

<script>
import FinalDispatchRequestActions from "./components/FinalDispatchRequestActions.vue";
import {date, datetime} from "@/helpers/date";
import {price} from "@/helpers/numbers";

import AddressTable from "./components/AddressTable";
import DispatchRequests from "./components/DispatchRequests";
import AddSalesOrderLine from "./components/AddSalesOrderLine";

import ShowDispatchRequestMetadata from "./components/modals/ShowDispatchRequestMetadata.vue";
import ShowSalesOrderMetadata from "./components/modals/ShowSalesOrderMetadata.vue";

import DrqButtons from "./components/DrqButtons.vue";

import { isOrderCancelled } from "../../helpers/salesOrders"

import OrderCommentsForm from '@/components/OrderComments/OrderCommentsForm.vue';
import OrderCommentsHistory from '@/components/OrderComments/OrderCommentsHistory.vue';

export default {
    name: "OrderDetailOverview",
    components: {
        FinalDispatchRequestActions,
        ShowDispatchRequestMetadata,
        ShowSalesOrderMetadata,
        AddSalesOrderLine,
        DispatchRequests,
        AddressTable,
        DrqButtons,
        OrderCommentsForm,
        OrderCommentsHistory,
    },
    props: {
        salesOrder: {required: true}
    },

    data() {
        return {
            isLoadingDispatchRequestActions: true,
            isDispatchRequestsLoading: false,
            isSavingComment: false,
            hasReroutableDispatchRequests: false,
            dispatchRequestRerouteMode: false,
            dispatchRequestRerouteModeConfirmClose: true,

            addressBilling:  {},
            addressShipping: {},
            dispatchRequests: null,
            editComment: null,

            editingAddressBilling:  false,
            editingAddressShipping: false,
            savingAddressBilling:   false,
            savingAddressShipping:  false,

            finalDispatchRequestAction: null,

            showAllPayments: false,
            isPaymentUpdating: false,
            editPayment: null,
            currentPaymentMethodIdentifier: '',
            selectedPaymentMethodIdentifier: '',
            selectedPaymentMethodName: '',
            allowedPaymentMethods: [],

            invoice_fields: [
                {
                    key: 'salesOrder_shippingGrossAmount',
                    label: 'Verzendkosten',
                },
                {
                    key: 'salesOrder_paymentGrossAmount',
                    label: 'Betaalkosten',
                },
                {
                    key: 'totalTaxAmount',
                    label: 'BTW',
                },
                {
                    key: 'salesOrder_grandTotal',
                    label: 'Totaal',
                },
            ],
        }
    },

    computed: {
        paymentsFields() {
            return [
                {key: 'datetime', label: 'Datum / tijd'},
                'Methode',
                'Status',
                'Bedrag',
                {key: 'actions', label: ''},
            ]
        },
        payments() {
            return this.salesOrder.sales_order_payments
                .filter(payment => ['not_mapped', 'redirected'].indexOf(payment.payment_status) === -1)
                .filter(payment => this.showAllPayments || !payment.is_replaced)
                .map(payment => {
                    if (!payment.payment_service_provider_account) payment.payment_service_provider_account = {payment_service_provider: {}}
                    if (!payment.payment_service_provider_account_method) payment.payment_service_provider_account_method = {}

                    let amount
                    try { amount = JSON.parse(payment.json_blob).payment.paymentOutput.acquiredAmount.amount / 100 }
                    catch (e) { amount = null }

                    return {
                        'id': payment.id,
                        'datetime': payment.created_at,
                        'is_replaced': payment.is_replaced,
                        'Methode': payment,
                        'Status': payment.payment_status,
                        'Bedrag': amount,
                    }
                })
        },
        totalTaxAmount() {
            let taxAmount = parseFloat(this.salesOrder.tax_amount)
            if (this.salesOrder.metadata?.surcharges?.tax_amount) {
                taxAmount += parseFloat(this.salesOrder.metadata.surcharges.tax_amount)
            }
            return taxAmount
        },

        /**
         * Computed property that checks if the user has permission to inspect the metadata.
         * @returns {boolean}
         */
        canInspectMetadata() {
          return this.hasPermission('orders.metadata.inspect');
        },
        canUnableToShipFinalLines() {
            return !!FinalDispatchRequestActions.methods.getApplicableDispatchRequests(
                    this.salesOrder.current_dispatch_requests,
                    FinalDispatchRequestActions.methods.getAllowedStatusesForAction('unable-to-ship-final')
                ).length
                && this.hasPermission('orders.dispatchrequests.unabletoshipfinal');
        },

        informationFieldItems() {
            const salesOrder = this.salesOrder;
            return [
                {
                    'salesOrder_identifier':        salesOrder.identifier,
                    'customer_identifier':          salesOrder.customer_identifier,
                    'loyalty_id':                   salesOrder.metadata?.loyalty_id || '-',
                    'sales_formula_identifier':     salesOrder.sales_formula_identifier,
                    'sales_channel_identifier':     salesOrder.sales_channel_identifier,
                    'logistics_scenario':           salesOrder.logistics_scenario,
                    'max_promised_delivery_date':   salesOrder.metadata?.max_promised_delivery_date || '-',
                    'coupon_codes':                 salesOrder.metadata?.coupon_codes || '-',
                    'placed_at':                    this.datetime(salesOrder.placed_at),
                    'created_at':                   this.datetime(salesOrder.created_at),
                    'promised_delivery_date':       this.date(salesOrder.promised_delivery_date),
                    'fulfilment_method':            this.$t(`omt.gui.orders.label.fulfilment-method.${salesOrder.fulfilment_method}`),
                    'current_fulfilment_method':    this.$t(`omt.gui.orders.label.fulfilment-method.${salesOrder.current_fulfilment_method}`),
                    'responsible_location':         this.getCurrentResponsibleLocation({ salesOrder }),
                    'responsible_location_history': this.getHistoricResponsibleLocations({ salesOrder }),
                    'invoices':                     this.getSalesOrderInvoices({ salesOrder }),
                    'payment_methods':              this.getSalesOrderPaymentMethods({ salesOrder }),
                    'purchase_order_nr':            salesOrder.metadata?.purchase_order_nr,
                    'order_type':                   (salesOrder.order_type) ? this.$t(`omt.gui.orders.order-types.${salesOrder.order_type}`) : null,
                }
            ];
        },

        /**
         * Information fields that are selected via the preferences GUI. Used to determine the fields that are
         * displayed in the general information block on top of the page.
         */
        informationFields() {
            const selectedFields = this.$store.getters.getFrontendParameterValue('order-details', 'information-fields') ||
                                    ['created_at', 'updated_at'];

            return selectedFields.map(field => ({
                key: field,
                label: this.$t(`omt.gui.orders.overview.information-fields.${field}`)
            }))
        },

        invoice_items() {
            return [
                {
                    salesOrder_shippingGrossAmount: price(this.salesOrder.shipping_gross_amount),
                    salesOrder_paymentGrossAmount: price(this.salesOrder.payment_gross_amount),
                    totalTaxAmount: price(this.totalTaxAmount),
                    salesOrder_grandTotal: price(this.salesOrder.grand_total),
                }
            ];
        },

        useIconForCancelledOrder() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'cancelled-orders-show-icon');
        },

        strikeThroughCancelledOrder() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'cancelled-orders-strikethrough');
        },

        showDrqButtonsOnTop() {
            // We're using indexOf on its string value because value can be 'top', 'bottom' or 'top-bottom'.
            return this.$store.getters.getFrontendParameterValue('order-details', 'drq-buttons-position').indexOf('top') !== -1;
        },

        showDrqButtonsOnBottom() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'drq-buttons-position').indexOf('bottom') !== -1;
        },

        drqButtonsAlignment() {
            return this.$store.getters.getFrontendParameterValue('order-details', 'drq-buttons-alignment');
        },
    },

    mounted() {
        document.title = `Order ${this.salesOrder.identifier} | OmniStock OMS`

        this.dispatchRequests = this.salesOrder.current_dispatch_requests_with_paused

        if (
            this.hasPermission('orders.dispatchrequests.include.reroutedreplaceddispatchrequests')
            && this.salesOrder.last_rerouted_dispatch_request
            && window.location.hash.includes('include_rerouted_replaced_dispatch_requests=1')
        ) {
            this.dispatchRequests = this.dispatchRequests.concat(this.salesOrder.last_rerouted_dispatch_request)
        }

        this.addressBilling  = this.getAddressByType('billing');
        this.addressShipping = this.getAddressByType('shipping');

        this.$store.getters.getDispatchRequestAllowedStatusChanges
            .then(() => this.isLoadingDispatchRequestActions = false)

        this.$store.getters.getPaymentsAllowedMethodChanges

    },

    methods: {
        date,
        datetime,
        isOrderCancelled,
        salesOrderLineAdded() {
            this.$emit('dataChanged')
            this.$bvModal.hide('add-order-line-modal')
        },
        reloadDispatchRequests() {
            this.isDispatchRequestsLoading = true
            this.$emit('dataChanged')
        },
        getAddressByType(type) {
            try {
                return this.salesOrder.addresses.filter(a => a.address_type === type)[0]
            } catch (e) {
                return {}
            }
        },

        /**
         * Updates the address with the specified type in the Sales Order object and component's local state.
         *
         * @param {string} type - The type of address ('billing' or 'shipping') to update.
         * @param {object} newAddress - The updated address object to replace the existing address.
         * @returns {object|null} - The updated address object if found, or null if not found or an error occurred.
         * @throws {Error} If an error occurs during the update.
         */
        updateAddressByType(type, newAddress) {
            try {
                const address = this.salesOrder.addresses.find(a => a.address_type === type);

                if (address) {
                    // Update the address in the Sales Order object
                    Object.assign(address, newAddress);

                    // Also update this.addressBilling or this.addressShipping, which are used for the component's local state.
                    switch (type) {
                        case 'billing':
                            this.addressBilling = newAddress;
                            break;
                        case 'shipping':
                            this.addressShipping = newAddress;
                            break;
                    }
                }

                return address; // Returns the updated address object or null if not found
            } catch (e) {
                return null;
            }
        },

        toggleRerouteMode(confirmClose = true) {
            if (!this.dispatchRequestRerouteMode) {
                this.dispatchRequestRerouteMode = true
                return false
            }

            this.dispatchRequestRerouteModeConfirmClose = confirmClose
            this.dispatchRequestRerouteMode = false
        },
        exitRerouteMode(event) {
            if (this.dispatchRequestRerouteModeConfirmClose && this.hasReroutableDispatchRequests) {
                if (event) event.preventDefault()

                this.$bvModal.msgBoxConfirm("Weet je zeker dat je reroute mode wilt verlaten en alle wijzigingen wilt weggooien?", {
                    title: 'Rerouting',
                    centered: true,
                    dialogClass: "shadow-none",
                    autoFocusButton: "ok"
                })
                    .then(confirmed => {
                        if (confirmed) {
                            this.dispatchRequestRerouteModeConfirmClose = false
                            this.$bvModal.hide('rerouting-modal')
                        }
                    })

                return false
            }

            this.dispatchRequestRerouteModeConfirmClose = true

            if (this.hasReroutableDispatchRequests) {
                this.reloadDispatchRequests()
            }
        },
        

        editAddressInformation(type) {
            if (type !== 'billing' && type !== 'shipping') {
              return;
            }

            this.editingAddressBilling  = (type === 'billing');
            this.editingAddressShipping = (type === 'shipping');
        },

        cancelEditAddressInformation(type) {
            if (type !== 'billing' && type !== 'shipping') {
              return;
            }

            this.editingAddressBilling  = false;
            this.editingAddressShipping = false;
        },

        saveAddressInformation(type, address) {
            if (type !== 'billing' && type !== 'shipping') {
              return;
            }

            this.savingAddressBilling  = (type === 'billing');
            this.savingAddressShipping = (type === 'shipping');

             const endpoint = `/api/rest/sales-orders/${this.salesOrder.identifier}`

             this.$http.patch(endpoint, {
               [`${type}_address`]: address
             })
              .then(response => {
                this.$alert(`Adres opgeslagen`, 'success');
                this.editingAddressBilling  = false;
                this.editingAddressShipping = false;

                this.updateAddressByType(type, address);
              })
              .catch(error => {
                this.$alert(`Fout bij het opslaan van het adres`, 'danger');
                console.error(`Fout bij het opslaan van het adres: ${error}`);
              })
              .finally(() => {
                  this.savingAddressBilling   = false;
                  this.savingAddressShipping  = false;
              });
        },
        openFinalDispatchRequestActionModal(action) {
            this.finalDispatchRequestAction = action
            this.$bvModal.show('final-dispatch-request-actions-modal')
        },
        openLink(url) {
            window.open(url, '_blank')
        },

        getCurrentResponsibleLocation({ salesOrder }) {
            if (!salesOrder.responsible_location_history || salesOrder.responsible_location_history.length === 0) {
                return '-';
            }
            const lastLocation = salesOrder.responsible_location_history[salesOrder.responsible_location_history.length - 1].location;
            return `${lastLocation.display_name} (${lastLocation.identifier})`;
        },

        getHistoricResponsibleLocations({ salesOrder }) {
            if (!salesOrder.responsible_location_history || salesOrder.responsible_location_history.length === 0) {
                return '-';
            }
            return salesOrder.responsible_location_history
                .slice(0, -1) // Exclude the last element, as the last location of the history is also the current location
                .map(location => `${location.location.display_name} (${location.location.identifier})`)
                .join(', ');
        },

        getSalesOrderInvoices({ salesOrder }) {
            if (!salesOrder.invoices || salesOrder.invoices.length === 0) {
                return '-';
            }
            return salesOrder.invoices
                .map(invoice => ({
                    txt: `${invoice.identifier} (${this.datetime(invoice.created_at)})`,
                    link: invoice.link
                  })
                );
        },

        /**
         * Gets all unique payment method names for a sales order.
         * @param {*} param0 Object with salesOrder as a property
         * @returns {string} Comma-separated string of unique payment method names
         */
        getSalesOrderPaymentMethods({ salesOrder }) {
            const payments = salesOrder['sales_order_payments'];
            const uniquePaymentMethods = Array.from(new Set(payments.map(payment => payment['payment_service_provider_account_method']
                ? payment['payment_service_provider_account_method']['display_name']
                : ''
            ))).filter(payment => payment !== '');

            if (uniquePaymentMethods.length === 0) {
                return '-';
            }

            return Array.from(uniquePaymentMethods).join(', ').trim();
        },

        handleSalesOrderCommentSaved() {
            this.$bvModal.hide('add-comment-form');
            this.$refs["orderCommentsHistory"].loadCommentHistory();
        },

        openEditCommentModal(comment) {
            this.editComment = comment;
            this.$bvModal.show('add-comment-form');
        },

        openEditPaymentModal(payment) {
            this.editPayment = payment
            this.currentPaymentMethodIdentifier = payment.Methode.payment_service_provider_account_method.identifier
            this.selectedPaymentMethodIdentifier = this.currentPaymentMethodIdentifier

            this.allowedPaymentMethods = this.$store.state.paymentsAllowedMethodChanges[this.currentPaymentMethodIdentifier]
            if (this.allowedPaymentMethods.length  === 1) {
                this.selectedPaymentMethodIdentifier = this.allowedPaymentMethods[0].identifier
                this.selectedPaymentMethodName = this.allowedPaymentMethods[0].display_name
            }
            this.$bvModal.show('updatePaymentModal')
        },

        isPaymentEditable(payment) {
            if (payment.is_replaced || !this.hasPermission('orders.payments.edit')) {
                return false;
            }
            let currentMethod = payment.Methode.payment_service_provider_account_method.identifier
            return this.$store.state.paymentsAllowedMethodChanges[currentMethod]?.length > 0
        },

        updatePayment() {
            this.isPaymentUpdating = true

            let url = `/api/rest/sales-orders/${this.salesOrder.identifier}/payments/${this.editPayment.id}`
            let requestData = {
                payment_method_identifier: this.selectedPaymentMethodIdentifier
            }

            this.$http.patch(url, requestData)
                .then(response => {
                    this.reloadPayments()
                    this.$alert('De betaling is gewijzigd')
                })
                .catch(error => {
                    this.isPaymentUpdating = false
                    this.$alert('Er is iets misgegaan bij het opslaan', 'danger', true)
                })
        },

        reloadPayments() {
            this.$http.get(`/api/rest/sales-orders/${this.salesOrder.identifier}/payments`)
                .then(response => {
                    this.salesOrder.sales_order_payments = response.data
                })
                .catch(error => {
                    this.$alert('Er is iets misgegaan bij het ophalen van de betalingen', 'danger', true)
                })
            .finally(
                this.isPaymentUpdating = false
            )
        },

    },
}
</script>

<style scoped lang="scss">
table td {
    text-align: left !important;
}
.editable-card-title {
    display: flex;
    gap: .5rem;
    justify-content: space-between;
}
.text-decoration-line-through { // class name adopted from Bootstrap v5.0.2
    text-decoration: line-through;
}
</style>
